/**
 * User Management Component
 * Table for managing Echo users with role promotion/demotion
 */

import React, { useState, useCallback } from 'react';
import { Card, Button, Stack, Flex, useTheme, spacing, fontSize } from '@fti/design-system';
import { ConfirmDeleteModal } from './ConfirmDeleteModal';
import { MatterAssignmentEditor } from './MatterAssignmentEditor';
import { useUserMatterAssignments } from '../../hooks/useUserMatterAssignments';
import type { EchoUser, EchoMatter } from '../../api/types/echo';

export interface UserManagementProps {
  users: EchoUser[];
  currentUserEmail: string;
  allMatters?: EchoMatter[];
  isLoading?: boolean;
  onPromoteToAdmin: (userId: number) => Promise<void>;
  onDemoteToUser: (userId: number) => Promise<void>;
  onDeleteUser: (userId: number) => Promise<void>;
}

/**
 * Component for managing Echo users
 */
export function UserManagement({
  users,
  currentUserEmail,
  allMatters = [],
  isLoading = false,
  onPromoteToAdmin,
  onDemoteToUser,
  onDeleteUser,
}: UserManagementProps) {
  const { theme } = useTheme();

  const [updatingUserId, setUpdatingUserId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<EchoUser | null>(null);
  const [expandedUserId, setExpandedUserId] = useState<number | null>(null);

  const matterAssignments = useUserMatterAssignments(expandedUserId);

  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;
  const successColor = theme.colors.semantic.success.default;
  const infoColor = theme.colors.semantic.info.default;

  const handleRoleChange = useCallback(async (user: EchoUser) => {
    setUpdatingUserId(user.id);
    try {
      if (user.role === 'admin') {
        await onDemoteToUser(user.id);
      } else {
        await onPromoteToAdmin(user.id);
      }
    } finally {
      setUpdatingUserId(null);
    }
  }, [onPromoteToAdmin, onDemoteToUser]);

  const handleDeleteClick = useCallback((user: EchoUser) => {
    setDeleteTarget(user);
  }, []);

  const handleDeleteClose = useCallback(() => {
    setDeleteTarget(null);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (deleteTarget) {
      await onDeleteUser(deleteTarget.id);
    }
  }, [deleteTarget, onDeleteUser]);

  const getRoleBadgeStyle = (role: string) => {
    const isAdmin = role === 'admin';
    return {
      padding: `2px ${spacing.sm}`,
      borderRadius: '4px',
      fontSize: fontSize.xs,
      fontWeight: 600,
      background: isAdmin ? `${infoColor}15` : `${successColor}15`,
      color: isAdmin ? infoColor : successColor,
    };
  };

  // Loading state
  if (isLoading) {
    return (
      <Card variant="outlined">
        <Card.Body>
          <Flex justify="center" align="center" style={{ minHeight: '200px' }}>
            <p style={{ color: mutedColor }}>Loading users...</p>
          </Flex>
        </Card.Body>
      </Card>
    );
  }

  // Empty state
  if (users.length === 0) {
    return (
      <Card variant="outlined">
        <Card.Body>
          <Flex justify="center" align="center" style={{ minHeight: '200px' }}>
            <Stack gap="sm" align="center">
              <span style={{ fontSize: fontSize['2xl'] }}>👥</span>
              <p style={{ margin: 0, color: mutedColor }}>No users found</p>
            </Stack>
          </Flex>
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      <div
        style={{
          border: `1px solid ${theme.colors.border.default}`,
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        {/* Table Header */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 120px 150px 140px 100px',
            padding: `${spacing.sm} ${spacing.md}`,
            background: theme.colors.background.paper,
            borderBottom: `1px solid ${theme.colors.border.default}`,
            fontSize: fontSize.xs,
            fontWeight: 600,
            color: mutedColor,
            textTransform: 'uppercase',
          }}
        >
          <div>Email</div>
          <div>Role</div>
          <div>Role Action</div>
          <div>Matters</div>
          <div>Delete</div>
        </div>

        {/* Table Rows */}
        {users.map((user) => {
          const isSelf = user.email === currentUserEmail;
          const isUpdating = updatingUserId === user.id;

          const isExpanded = expandedUserId === user.id;

          return (
            <React.Fragment key={user.id}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 120px 150px 140px 100px',
                padding: `${spacing.sm} ${spacing.md}`,
                borderBottom: isExpanded ? 'none' : `1px solid ${theme.colors.border.default}`,
                alignItems: 'center',
                fontSize: fontSize.sm,
                background: isSelf ? `${theme.colors.primary[600]}05` : 'transparent',
              }}
            >
              {/* Email */}
              <div>
                <span style={{ fontWeight: 500, color: textColor }}>{user.email}</span>
                {isSelf && (
                  <span
                    style={{
                      marginLeft: spacing.sm,
                      fontSize: fontSize.xs,
                      color: mutedColor,
                      fontStyle: 'italic',
                    }}
                  >
                    (you)
                  </span>
                )}
              </div>

              {/* Role Badge */}
              <div>
                <span style={getRoleBadgeStyle(user.role)}>
                  {user.role === 'admin' ? 'Admin' : 'User'}
                </span>
              </div>

              {/* Role Action */}
              <div>
                {isSelf ? (
                  <span style={{ fontSize: fontSize.sm, color: mutedColor, fontStyle: 'italic' }}>
                    Can't modify self
                  </span>
                ) : (
                  <Button
                    variant="outline"
                    color={user.role === 'admin' ? 'warning' : 'primary'}
                    size="sm"
                    onClick={() => handleRoleChange(user)}
                    disabled={isUpdating}
                  >
                    {isUpdating
                      ? 'Updating...'
                      : user.role === 'admin'
                      ? 'Demote'
                      : 'Promote'}
                  </Button>
                )}
              </div>

              {/* Matters */}
              <div>
                {user.role === 'admin' ? (
                  <span style={{ fontSize: fontSize.xs, color: mutedColor, fontStyle: 'italic' }}>
                    Full access
                  </span>
                ) : (
                  <Button
                    variant="outline"
                    color="primary"
                    size="sm"
                    onClick={() => setExpandedUserId(isExpanded ? null : user.id)}
                    disabled={isUpdating}
                  >
                    {isExpanded ? 'Hide' : 'Manage'}
                  </Button>
                )}
              </div>

              {/* Delete */}
              <div>
                {isSelf ? (
                  <span style={{ fontSize: fontSize.sm, color: mutedColor }}>—</span>
                ) : (
                  <Button
                    variant="outline"
                    color="error"
                    size="sm"
                    onClick={() => handleDeleteClick(user)}
                    disabled={isUpdating}
                  >
                    Delete
                  </Button>
                )}
              </div>
            </div>

            {/* Expanded Matter Assignments Panel */}
            {isExpanded && allMatters.length > 0 && (
              <div
                style={{
                  padding: `${spacing.sm} ${spacing.md} ${spacing.md}`,
                  borderBottom: `1px solid ${theme.colors.border.default}`,
                  background: `${theme.colors.background.paper}`,
                }}
              >
                <MatterAssignmentEditor
                  allMatters={allMatters}
                  assignedMatterIds={matterAssignments.assignedMatterIds}
                  onSave={(matterIds) => matterAssignments.updateAssignments(user.id, matterIds)}
                  isLoading={matterAssignments.isLoading}
                  isSaving={matterAssignments.isSaving}
                />
              </div>
            )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={!!deleteTarget}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
        title="Delete User"
        message="Are you sure you want to delete this user? They will need to sign in again to be re-added."
        itemName={deleteTarget?.email}
        confirmButtonText="Delete User"
      />
    </>
  );
}

export default UserManagement;
